/* ------------------------------------------------------------------
  File: MegaMenu.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-07-01
------------------------------------------------------------------ */

// Purpose:
//   - Renders a reusable desktop mega menu.
//   - Uses menu data from navConfig.
//   - Supports intro content, grouped navigation columns,
//     nested drill-down lists, and a promotional feature card.

import { useState } from "react";
import { Link } from "react-router-dom";
import { LuArrowRight, LuChevronRight, LuChevronLeft } from "react-icons/lu";
import "./MegaMenu.css";
import Button from "../../../ui/Buttons";

function MegaMenu({ menu }) {
  const [activeDrilldown, setActiveDrilldown] = useState(null);

  if (!menu) return null;

  const visibleColumns = menu.columns?.map((column, index) => {
    if (activeDrilldown?.columnIndex === index) {
      return {
        ...column,
        title: column.title,
        isDrilldown: true,
        items: activeDrilldown.items,
      };
    }

    return column;
  });

  const handleDrilldownOpen = (columnIndex, item) => {
    if (!item.children?.length) return;

    setActiveDrilldown({
      columnIndex,
      parentLabel: item.label,
      items: item.children,
    });
  };

  const handleDrilldownBack = () => {
    setActiveDrilldown(null);
  };

  return (
    <div className="acc-mega-menu" role="menu">
      <div className="acc-mega-menu__inner l-container l-container--wide l-grid l-grid--12">
        {/* Intro Column */}
        <section className="acc-mega-menu__intro l-span--3 l-stack l-stack--sm">
          {menu.eyebrow && (
            <h6 className="acc-mega-menu__intro-eyebrow">{menu.eyebrow}</h6>
          )}

          {menu.title && (
            <h5 className="acc-mega-menu__intro-title ">{menu.title}</h5>
          )}

          {menu.description && (
            <p className="acc-mega-menu__intro-description u-type-small">{menu.description}</p>
          )}

          {menu.cta?.label && menu.cta?.to && (
            <Button
              variant="link"
              size="sm"
              to={menu.cta.to}
              icon={<LuArrowRight />}
              className="acc-mega-menu__intro-button"
            >
              {menu.cta.label}
            </Button>
          )}
        </section>

        {/* Menu Columns */}
        <div className="acc-mega-menu__columns l-span--6 l-grid l-grid--2">
          {visibleColumns?.map((column, columnIndex) => (
            <section className="acc-mega-menu__column l-stack l-stack--sm" key={column.title}>
              <h6 className="acc-mega-menu__column-title ">{column.title}</h6>

              {column.isDrilldown && (
                <Button
                  variant="link"
                  size="sm"
                  icon={<LuChevronLeft aria-hidden="true" />}
                  iconPosition="left"
                  className="acc-mega-menu__back--btn"
                  onClick={handleDrilldownBack}
                >
                  Go Back
                </Button>
              )}

              <ul className="acc-mega-menu__list l-stack l-stack--xs">
                {column.items?.map((item) => {
                  const hasChildren = item.children?.length > 0;

                  return (
                    <li className="acc-mega-menu__item" key={item.label}>
                      {hasChildren ? (
                        <Button
                          as={Link}
                          variant="link"
                          icon={<LuChevronRight aria-hidden="true" />}
                          className="acc-mega-menu__link acc-mega-menu__link--btn"
                          onClick={() => handleDrilldownOpen(columnIndex, item)}
                        >
                          {item.label}
                        </Button>
                      ) : (
                        <Button
                          as={Link}
                          to={item.to || "#"}
                          variant="link"
                          icon={<LuChevronRight aria-hidden="true" />}
                          className="acc-mega-menu__link acc-mega-menu__link--btn"
                        >
                          {item.label}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>

              {column.cta?.label && column.cta?.to && (
                <Button
                  variant="link"
                  size="sm"
                  to={column.cta.to}
                  icon={<LuArrowRight aria-hidden="true" />}
                  className="acc-mega-menu__column-button"
                >
                  {column.cta.label}
                </Button>
              )}
            </section>
          ))}
        </div>

        {/* Feature Card */}
        {menu.feature && (
          <aside className="acc-mega-menu__feature l-span--3">
            <div className="acc-mega-menu__feature-media u-media-frame">
              {menu.feature.image && (
                <img
                  src={menu.feature.image}
                  alt={menu.feature.imageAlt || ""}
                  className="acc-mega-menu__feature-img"
                />
              )}
            </div>

            <div className="acc-mega-menu__feature-body l-stack l-stack--sm">
              {menu.feature.title && (
                <h5 className="acc-mega-menu__feature-title ">
                  {menu.feature.title}
                </h5>
              )}

              {menu.feature.description && (
                <p className="acc-mega-menu__feature-text u-type-small">
                  {menu.feature.description}
                </p>
              )}

              {menu.feature.cta?.label && menu.feature.cta?.to && (
                <Button
                  to={menu.feature.cta.to}
                  variant="link"
                  size="sm"
                  icon={<LuArrowRight aria-hidden="true" />}
                  className="acc-mega-menu__feature-link"
                >
                  {menu.feature.cta.label}
                </Button>
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

export default MegaMenu;